import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sun, LogOut, Users, Sparkles, CheckCircle2, Home, Building2, Trash2, Phone, Mail, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  novo: { label: "Novo", cls: "bg-primary/15 text-primary" },
  em_contato: { label: "Em contato", cls: "bg-accent/20 text-accent-foreground" },
  convertido: { label: "Convertido", cls: "bg-emerald-100 text-emerald-700" },
  perdido: { label: "Perdido", cls: "bg-rose-100 text-rose-600" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data); setStats(s.data);
    } catch { toast.error("Erro ao carregar leads."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user === false) { nav("/admin/login"); return; }
    if (user) load();
  }, [user, load, nav]);

  const changeStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    load();
  };
  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    toast.success("Lead removido."); load();
  };
  const doLogout = async () => { await logout(); nav("/admin/login"); };

  if (user === null || loading) {
    return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Carregando...</div>;
  }

  const cards = stats ? [
    { icon: Users, label: "Total de leads", value: stats.total },
    { icon: Sparkles, label: "Novos", value: stats.novos },
    { icon: CheckCircle2, label: "Convertidos", value: stats.convertidos },
    { icon: Home, label: "Residencial", value: stats.residencial },
    { icon: Building2, label: "Comercial", value: stats.comercial },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-secondary text-secondary-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground"><Sun size={20} strokeWidth={2.5} /></span>
            <span className="font-display font-extrabold tracking-tight">Green Era • Leads</span>
          </div>
          <button onClick={doLogout} className="inline-flex items-center gap-2 text-sm font-medium text-secondary-foreground/80 hover:text-primary transition-colors" data-testid="logout-btn">
            <LogOut size={16} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-secondary tracking-tighter">Leads recebidos</h1>
            <p className="text-muted-foreground text-sm mt-1">Bem-vindo, {user.name}</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium hover:border-primary transition-colors" data-testid="refresh-btn">
            <RefreshCw size={15} /> Atualizar
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {cards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-surface border border-border/60 p-5" data-testid={`stat-${i}`}>
              <c.icon size={20} className="text-primary mb-3" />
              <div className="font-display font-extrabold text-3xl text-secondary tracking-tight">{c.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="rounded-2xl bg-surface border border-border/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="px-5 py-4 font-medium">Nome</th>
                  <th className="px-5 py-4 font-medium">Contato</th>
                  <th className="px-5 py-4 font-medium">Cidade</th>
                  <th className="px-5 py-4 font-medium">Tipo</th>
                  <th className="px-5 py-4 font-medium">Conta</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-16 text-center text-muted-foreground">Nenhum lead ainda.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-border/60 hover:bg-background/60 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4 font-medium text-secondary">{l.name}</td>
                    <td className="px-5 py-4 text-muted-foreground">
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1.5"><Phone size={13} /> {l.phone}</span>
                        {l.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {l.email}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">{l.city || "—"}</td>
                    <td className="px-5 py-4 capitalize text-muted-foreground">{l.type}</td>
                    <td className="px-5 py-4 text-muted-foreground">{l.bill_value ? `R$ ${l.bill_value}` : "—"}</td>
                    <td className="px-5 py-4">
                      <select value={l.status} onChange={(e) => changeStatus(l.id, e.target.value)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold border-0 cursor-pointer ${STATUS[l.status]?.cls || ""}`}
                        data-testid={`lead-status-${l.id}`}>
                        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => remove(l.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`lead-delete-${l.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
